import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Carousel } from "react-bootstrap";
import {
  MagnifyingGlassIcon,
  HeartIcon as HeartOutline,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  DocumentIcon,
  XMarkIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon as HeartSolid } from "@heroicons/react/24/solid";
import NavPointsIcon from "../assets/Icons/points-icon.svg";
import RightArrowCard from "../assets/Icons/right-arrow.svg";
import Navbar from "./navbar";
import Footer from "./footer";
import { ArrowLeftIcon } from "lucide-react";

// Section and DetailItem components remain unchanged
const Section = ({ title, isOpen, toggle, children }) => (
  <div className="border-b border-gray-200">
    <button
      onClick={toggle}
      className="w-full flex justify-between items-center py-2 text-left text-lg font-medium text-[#676666]"
    >
      <span>{title}</span>
      <ChevronDownIcon
        className={`h-5 w-5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
      />
    </button>
    {isOpen && <div className="p-4">{children}</div>}
  </div>
);

const DetailItem = ({ label, value }) => (
  <div className="flex justify-between py-1">
    <span className="text-gray-600">{label}:</span>
    <span className="text-gray-800">{value || "N/A"}</span>
  </div>
);

const ViewAllProfiles = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [ageFilter, setAgeFilter] = useState("");
  const [casteFilter, setCasteFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [sort, setSortProfile] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [favorites, setFavorites] = useState([]);
  
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [profileToView, setProfileToView] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportingProfileId, setReportingProfileId] = useState("");
  const [ShowPopupRechargePoints, setShowPopupRechargePoints] = useState(false);
  const [openSections, setOpenSections] = useState({
    personal: true,
    family: true,
    horoscope: true,
    preferences: true,
    documents: true,
  });
  
 const [viewedProfiles, setViewedProfiles] = useState([]);

 useEffect(() => {
    const fetchViewedProfiles = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) return;
        
        const response = await fetch('https://backend.newbrindha.com/matrimony/viewed-profiles', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setViewedProfiles(data.viewed_profiles || []);
        }
      } catch (error) {
        console.error('Error fetching viewed profiles:', error);
      }
    };
    
    fetchViewedProfiles();
  }, []);


 
  
  useEffect(() => {
    const savedFavorites = localStorage.getItem('favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);
  
  

  const handleSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    [],
  );

  const filterProfiles = (profileList) => {
    const safeProfileList = Array.isArray(profileList) ? profileList : [];
    return safeProfileList.filter((profile) => {
      const matchesSearch =
        profile.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        true;
      const matchesAge = ageFilter
        ? profile.age
            ?.toString()
            .toLowerCase()
            .startsWith(ageFilter.toLowerCase())
        : true;
      const matchesCaste = casteFilter
        ? profile.caste?.toLowerCase().startsWith(casteFilter.toLowerCase())
        : true;
      const matchesLocation = locationFilter
        ? profile.native?.toLowerCase().startsWith(locationFilter.toLowerCase())
        : true;
      return matchesSearch && matchesAge && matchesCaste && matchesLocation;
    });
  };

  const sortedProfiles = useMemo(() => {
    const sorted = [...filterProfiles(profiles)];
    if (sort === "A-Z") {
      sorted.sort((a, b) =>
        (a.full_name?.toLowerCase() || "").localeCompare(
          b.full_name?.toLowerCase() || "",
        ),
      );
    } else if (sort === "Z-A") {
      sorted.sort((a, b) =>
        (b.full_name?.toLowerCase() || "").localeCompare(
          a.full_name?.toLowerCase() || "",
        ),
      );
    } else if (sort === "Age-Asc") {
      sorted.sort((a, b) => (parseInt(a.age) || 0) - (parseInt(b.age) || 0));
    } else if (sort === "Age-Desc") {
      sorted.sort((a, b) => (parseInt(b.age) || 0) - (parseInt(a.age) || 0));
    }
    return sorted;
  }, [profiles, sort, searchTerm, ageFilter, casteFilter, locationFilter]);

  const handleSpendPoints = (profile) => {
  if (viewedProfiles.includes(profile.matrimony_id)) {
    setSelectedProfile(profile);
    return;
  }
  setProfileToView(profile);
  setShowConfirmPopup(true);
};

useEffect(() => {
  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://backend.newbrindha.com/matrimony/profiles', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch profiles');
      }
      
      const data = await response.json();
      console.log('API Response:', data); // Debug log
      
      if (data && Array.isArray(data.profiles)) {
        setProfiles(data.profiles);
        sessionStorage.setItem('viewAllProfiles', JSON.stringify(data.profiles));
      } else {
        console.error('Unexpected data format:', data);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error(t('errorFetchingProfiles'));
    }
  };

  // Check sessionStorage first
  const savedProfiles = sessionStorage.getItem('viewAllProfiles');
  if (savedProfiles) {
    setProfiles(JSON.parse(savedProfiles));
  } else {
    fetchProfiles();
  }
}, [t]);

useEffect(() => {
  console.log('Profiles data:', profiles);
  console.log('Sorted profiles:', sortedProfiles);
  console.log('Filters:', { searchTerm, ageFilter, casteFilter, locationFilter });
}, [profiles, sortedProfiles, searchTerm, ageFilter, casteFilter, locationFilter]);

 const confirmSpendPoints = async () => {
  const token = localStorage.getItem('authToken');
  try {
    const spendResponse = await fetch('https://backend.newbrindha.com/wallet/spend', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        spend_requests: [{ profile_matrimony_id: profileToView.matrimony_id, points: 100 }],
      }),
    });

    const spendData = await spendResponse.json();
    if (spendResponse.ok) {
      if (spendData.token) localStorage.setItem('authToken', spendData.token);
      
      // Then mark as viewed
      const markViewedResponse = await fetch('https://backend.newbrindha.com/matrimony/mark-viewed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ profile_matrimony_ids: [profileToView.matrimony_id] }),
      });
      
      if (markViewedResponse.ok) {
        setViewedProfiles(prev => [...prev, profileToView.matrimony_id]);
        setSelectedProfile(profileToView);

        // Update points balance
        const balanceResponse = await fetch('https://backend.newbrindha.com/wallet/balance', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (balanceResponse.ok) {
          const balanceData = await balanceResponse.json();
          window.dispatchEvent(new CustomEvent('updatePoints', { detail: balanceData.points_balance }));
        }

        toast.success(t('pointsSpentSuccess'));
      }
    } else {
      setShowPopupRechargePoints(true);
    }
  } catch (error) {
    console.error('Error:', error.message);
    toast.error(t('pointsSpentError', { error: error.message }));
  } finally {
    setShowConfirmPopup(false);
    setProfileToView(null);
  }
};
  

  const toggleFavourite = async (profile) => {
    try {
      const token = localStorage.getItem("authToken");
      const profileId = profile.matrimony_id;
      const isCurrentlyFavorited = favorites.includes(profileId);
      const requestBody = {
        favorite_profile_ids: isCurrentlyFavorited ? [] : [profileId],
        unfavorite_profile_ids: isCurrentlyFavorited ? [profileId] : [],
      };

      const updatedFavorites = isCurrentlyFavorited
        ? favorites.filter((id) => id !== profileId)
        : [...favorites, profileId];
      setFavorites(updatedFavorites);
      //localStorage.setItem("favorites", JSON.stringify(updatedFavorites));

      const response = await fetch(
        "https://backend.newbrindha.com/matrimony/favorite-profiles",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody),
        },
      );

      if (!response.ok) throw new Error("Failed to update favorites");
    } catch (error) {
      console.error("Error updating favorites:", error);
      const savedFavorites = JSON.parse(
        localStorage.getItem("favorites") || "[]",
      );
      setFavorites(savedFavorites);
    }
  };

  const handleReportProfile = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        "https://backend.newbrindha.com/matrimony/user/report",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            reported_matrimony_id: reportingProfileId,
            reason: reportReason,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to report profile");
      }

      const data = await response.json();
      toast.success(data.message || t("reportSuccess"));
      setShowReportModal(false);
      setReportReason("");
      setReportingProfileId("");
    } catch (error) {
      console.error("Error reporting profile:", error);
      toast.error(error.message || t("reportError"));
    }
  };

  const openReportModal = (profileId) => {
    setReportingProfileId(profileId);
    setShowReportModal(true);
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const ProfileCard = ({ profile, index }) => {
  const [isMobileViewOpen, setIsMobileViewOpen] = useState(false);
  const isMobile = window.innerWidth < 640; // Adjust breakpoint as needed
  const photo = profile.photo || "https://images.unsplash.com/photo-1541823709867-1b206113eafd?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTMxfHxnaXJsfGVufDB8fDB8fHww";
  
  const verificationStatus = profile.verification_status || "pending";
  const verificationColor = verificationStatus === "approve" ? "bg-green-500" : "bg-red-500";
  const verificationText = verificationStatus === "approve" ? t("approved") : t("pending");

  const isViewed = viewedProfiles.includes(profile.matrimony_id);

  const toggleMobileView = () => {
    if (isMobile) {
      setIsMobileViewOpen(!isMobileViewOpen);
    }
  };

  return (
    <motion.div
      key={index}
      className="no-scrollbar relative rounded-2xl cursor-pointer w-full h-[280px] xs:h-[300px] sm:h-[320px] md:h-[340px] lg:h-[360px] overflow-hidden group flex-shrink-0"
      onClick={toggleMobileView}
    >
      {/* Verification Status */}
       <div
          className={`absolute z-30 ${verificationColor} text-white text-[10px] xs:text-xs px-1.5 py-0.5 
            xs:px-2 xs:py-1 rounded-full font-medium shadow-lg
            ${window.innerWidth < 640 ? 'top-2 left-2' : 'top-2 right-2'}
          `}
        >
          {verificationText}
        </div>

        {/* Viewed Badge */}
        {viewedProfiles.includes(profile.matrimony_id) && (
          <div
            className={`absolute z-30 bg-blue-500 text-white text-[10px] xs:text-xs 
              px-1.5 py-0.5 xs:px-2 xs:py-1 rounded-full font-medium shadow-lg
              ${window.innerWidth < 640 ? 'left-2 top-8' : 'left-2 top-2'}
            `}
          >
            {t("viewed")}
          </div>
        )}

      {/* Heart Icon - Always visible on mobile */}
      <div 
        className="absolute top-2 right-2 z-20 sm:hidden"
        onClick={(e) => {
          e.stopPropagation();
          toggleFavourite(profile);
        }}
      >
        {favorites.includes(profile.matrimony_id) ? (
          <HeartSolid className="h-5 w-5 text-red-500" />
        ) : (
          <HeartOutline className="h-5 w-5 text-white" />
        )}
      </div>

      {/* Mobile View - Show details when clicked */}
      {(isMobileViewOpen || !isMobile) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeInOut" }}
          className={`${isMobile ? 'flex' : 'hidden group-hover:flex'} flex-col absolute inset-0 bg-black/70 z-30 items-start justify-start text-white p-3 h-full w-full overflow-y-auto`}
        >
          {/* Desktop Heart Icon */}
          <div
            className="hidden sm:block absolute top-3 right-3 cursor-pointer z-10"
            onClick={(e) => {
              e.stopPropagation();
              toggleFavourite(profile);
            }}
          >
            {favorites.includes(profile.matrimony_id) ? (
              <HeartSolid className="h-6 w-6 text-red-500" />
            ) : (
              <HeartOutline className="h-6 w-6 text-white" />
            )}
          </div>

          <p className="text-left text-sm xs:text-base sm:text-lg font-bold mb-1 sm:mb-2">
            {profile.full_name}
          </p>
          <div className="flex flex-col items-start justify-start space-y-0.5 sm:space-y-1 text-xs xs:text-sm w-full break-words">
            <p className="m-0">
              {t("age")}: {profile.age}
            </p>
            <p className="m-0">
              {t("location")}: {profile.work_location}
            </p>
            <p className="m-0">
              {t("occupation")}: {profile.occupation}
            </p>
            <button className="flex items-center gap-1 sm:gap-2 mt-1 sm:mt-2 text-white font-medium">
              <img
                src={NavPointsIcon}
                alt="points-icon"
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
              {"500"}
            </button>
          </div>
         
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSpendPoints(profile);
            }}
            className="bg-[#fa6938] hover:bg-[#ff7142] active:bg-[#fa6938] mt-2 sm:mt-3 px-2 py-1 sm:px-3 sm:py-2 rounded-lg w-full text-xs sm:text-sm font-medium"
          >
            {isViewed ? t("viewDetails") : t("moreDetails")}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              openReportModal(profile.matrimony_id);
            }}
            className="mt-2 px-2 py-1 sm:px-3 sm:py-2 rounded-lg w-full text-xs sm:text-sm font-medium border border-white text-white hover:bg-black hover:text-white transition-colors duration-200"
          >
            {t("report")}
          </button>
        </motion.div>
      )}

      {/* Gradient overlay */}
      <div
        className="absolute w-full h-full z-10"
        style={{
          boxShadow: "rgba(5, 5, 5, 0.95) 0px -50px 36px -28px inset",
        }}
      />
      
      {/* Profile image */}
      <img
        src={photo}
        alt={profile.full_name}
        className="w-full h-full object-cover z-30 transition-all duration-300 ease-in-out group-hover:scale-105"
      />
      
      {/* Bottom info bar */}
      <div className="absolute m-2 bottom-0 left-0 right-0 bg-white/50 px-2 py-1 sm:px-3 sm:py-2 rounded-2xl text-black flex items-center justify-between z-40">
        <div>
          <h3 className="font-bold text-xs xs:text-sm sm:text-[16px] leading-tight">
            {profile.full_name}
          </h3>
          <p className="text-xs mb-1 text-semibold">
            {t("age")}: {profile.age}
          </p>
        </div>
        <div className="text-white">
          <img
            src={RightArrowCard}
            alt="RightArrowCard"
            className="h-4 w-6 sm:h-6 sm:w-8"
          />
        </div>
      </div>
    </motion.div>
  );
};

  return (
    <div className="min-h-screen flex bg-gradient-to-br from-orange-100 via-violet-200 to-indigo-300 bg-[length:400%_400%] animate-gradient px-0 flex-col">
      <div className="min-h-[100dvh] w-full flex-grow">
        <Navbar />
        <section className="sticky top-10 z-40 pt-10 px-4 pb-4 w-full">
          <article className="flex flex-col sm:flex-row gap-2">
            <div className="relative sm:w-52 md:w-full text-white py-2">
              <input
                type="text"
                placeholder={t("searchPlaceholder")}
                defaultValue={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 py-2 pr-6 sm:w-52 sm:pl-10 sm:pr-4 md:w-[350px] rounded-lg focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200 bg-white text-[#676666] border-2 border-gray-300 placeholder:text-[#676666] focus:outline-none focus:w-72"
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-[#676666] absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 sm:gap-2 md:gap-4 w-fit">
                <input
                  type="text"
                  placeholder={t("ageFilter")}
                  value={ageFilter}
                  onChange={(e) => setAgeFilter(e.target.value)}
                  className="w-full lg:w-32 px-2 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                />
                <input
                  type="text"
                  placeholder={t("casteFilter")}
                  value={casteFilter}
                  onChange={(e) => setCasteFilter(e.target.value)}
                  className="w-full lg:w-32 px-2 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                />
                <input
                  type="text"
                  placeholder={t("locationFilter")}
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="w-full lg:w-32 px-2 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-[#FFA500] focus:border-transparent transition-all duration-200"
                />
              </div>
              <div>
                <select
                  value={sort}
                  onChange={(e) => setSortProfile(e.target.value)}
                  className="w-auto sm:w-[100px] px-2 py-2 rounded-lg border-2 border-gray-300 focus:ring-2 text-[#676666]"
                >
                  <option value="">{t("sortBy")}</option>
                  <option value="A-Z">{t("sortAZ")}</option>
                  <option value="Z-A">{t("sortZA")}</option>
                  <option value="Age-Asc">{t("sortAgeAsc")}</option>
                  <option value="Age-Desc">{t("sortAgeDesc")}</option>
                </select>
              </div>
            </div>
          </article>
        </section>
        <motion.main
          className="z-30 bg-gradient-to-br from-orange-100 via-violet-200 to-indigo-300 bg-[length:400%_400%] animate-gradient p-4 md:p-6 min-h-screen"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-xl font-bold mb-6 text-[#676666]">
           {t("allProfiles")}
          </h2>

          {sortedProfiles.length === 0 ? (
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
              <p className="text-gray-600">{t("noProfilesFound")}</p>
              <motion.button
                onClick={() => navigate("/dashboard")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#fa6938] to-[#ff7142] hover:from-[#ff7142] hover:to-[#fa6938] text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <ArrowLeftIcon className="h-6 w-6" />
                {t("backToHome") || "Back to Home"}
              </motion.button>
            </div>
          ) : (
            <>
              <motion.div
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 md:gap-6 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {sortedProfiles.map((profile, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ scale: 1.03 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <ProfileCard profile={profile} index={index} />
                  </motion.div>
                ))}
              </motion.div>

              <div className="flex justify-center mt-8 pb-8">
                <motion.button
                  onClick={() => navigate("/dashboard")}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#fa6938] to-[#ff7142] hover:from-[#ff7142] hover:to-[#fa6938] text-white rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <ArrowLeftIcon className="h-6 w-6" />
                  {t("backToHome") || "Back to Home"}
                </motion.button>
              </div>
            </>
          )}
        </motion.main>
      </div>
      <AnimatePresence>
        {selectedProfile && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[999]"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <motion.div
              className="bg-white rounded-xl max-w-4xl w-full p-4 overflow-y-auto max-h-[90vh] shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-[18px] font-semibold text-[#676666]">
                  {t("profileDetails.profileDetails")}
                </h2>
                <button
                  onClick={() => setSelectedProfile(null)}
                  className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <XMarkIcon className="h-6 w-6 font-semibold text-red-500" />
                </button>
              </div>
              <div
                className="absolute top-4 right-4 sm:hidden z-20"
                onClick={() => toggleFavourite(selectedProfile)}
              >
                {favorites.includes(selectedProfile.matrimony_id) ? (
                  <HeartSolid className="h-6 w-6 text-red-500" />
                ) : (
                  <HeartOutline className="h-6 w-6 text-gray-500" />
                )}
              </div>
              <div className="mb-6">
                <Carousel
                  interval={3000}
                  className="w-full h-96 rounded-lg overflow-hidden shadow-lg"
                  indicators={(selectedProfile.photos?.length || 0) > 0}
                  prevIcon={<ChevronLeftIcon className="h-8 w-8 text-black" />}
                  nextIcon={<ChevronRightIcon className="h-8 w-8 text-black" />}
                >
                  {selectedProfile.photo && (
                    <Carousel.Item>
                      <div className="relative w-full h-96 flex items-center justify-center bg-gray-100">
                        <img
                          src={selectedProfile.photo}
                          alt="Profile"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = "/default-placeholder.png";
                            console.error("Failed to load profile photo");
                          }}
                        />
                      </div>
                    </Carousel.Item>
                  )}
                  {selectedProfile.photos?.map((photo, index) => (
                    <Carousel.Item key={index}>
                      <div className="relative w-full h-96 flex items-center justify-center bg-gray-100">
                        <img
                          src={photo}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src = "/default-placeholder.png";
                            console.error(`Failed to load photo ${index + 1}`);
                          }}
                        />
                      </div>
                    </Carousel.Item>
                  ))}
                  {!selectedProfile.photo &&
                    !selectedProfile.photos?.length && (
                      <Carousel.Item>
                        <div className="relative w-full h-96 flex items-center justify-center bg-gray-100">
                          <img
                            src="/default-placeholder.png"
                            alt="Placeholder"
                            className="w-full h-full object-contain"
                          />
                        </div>
                      </Carousel.Item>
                    )}
                </Carousel>
              </div>
              <div className="space-y-4">
                <Section
                  title={t("personalDetails")}
                  isOpen={openSections.personal}
                  toggle={() => toggleSection("personal")}
                >
                  <DetailItem
                    label={t("name")}
                    value={selectedProfile.full_name}
                  />
                  <DetailItem
                    label={t("matrimonyId")}
                    value={selectedProfile.matrimony_id}
                  />
                  <DetailItem label={t("age")} value={selectedProfile.age} />
                  <DetailItem
                    label={t("gender")}
                    value={selectedProfile.gender}
                  />
                  <DetailItem
                    label={t("dateOfBirth")}
                    value={selectedProfile.date_of_birth}
                  />
                  <DetailItem
                    label={t("height")}
                    value={selectedProfile.height}
                  />
                  <DetailItem
                    label={t("weight")}
                    value={selectedProfile.weight}
                  />
                  <DetailItem
                    label={t("email")}
                    value={selectedProfile.email}
                  />
                  <DetailItem
                    label={t("phone")}
                    value={selectedProfile.phone_number}
                  />
                  <DetailItem
                    label={t("motherTongue")}
                    value={selectedProfile.mother_tongue}
                  />
                  <DetailItem
                    label={t("native")}
                    value={selectedProfile.native}
                  />
                  <DetailItem
                    label={t("religion")}
                    value={selectedProfile.religion}
                  />
                  <DetailItem
                    label={t("caste")}
                    value={selectedProfile.caste}
                  />
                  <DetailItem
                    label={t("subCaste")}
                    value={selectedProfile.sub_caste}
                  />
                  <DetailItem
                    label={t("bloodGroup")}
                    value={selectedProfile.blood_group}
                  />
                </Section>
                <Section
                  title={t("familyProfessionalDetails")}
                  isOpen={openSections.family}
                  toggle={() => toggleSection("family")}
                >
                  <DetailItem
                    label={t("fatherName")}
                    value={selectedProfile.father_name}
                  />
                  <DetailItem
                    label={t("motherName")}
                    value={selectedProfile.mother_name}
                  />
                  <DetailItem
                    label={t("fatherOccupation")}
                    value={selectedProfile.father_occupation}
                  />
                  <DetailItem
                    label={t("motherOccupation")}
                    value={selectedProfile.mother_occupation}
                  />
                  <DetailItem
                    label={t("siblingCount")}
                    value={selectedProfile.sibling_count}
                  />
                  <DetailItem
                    label={t("occupation")}
                    value={selectedProfile.occupation}
                  />
                  <DetailItem
                    label={t("annualIncome")}
                    value={selectedProfile.annual_income}
                  />
                  <DetailItem
                    label={t("education")}
                    value={selectedProfile.education}
                  />
                  <DetailItem
                    label={t("company")}
                    value={selectedProfile.company}
                  />
                  <DetailItem
                    label={t("workLocation")}
                    value={selectedProfile.work_location}
                  />
                  <DetailItem
                    label={t("workCountry")}
                    value={selectedProfile.work_country}
                  />
                </Section>
                <Section
                  title={t("horoscopeDetails")}
                  isOpen={openSections.horoscope}
                  toggle={() => toggleSection("horoscope")}
                >
                  <DetailItem
                    label={t("nakshatra")}
                    value={selectedProfile.nakshatra}
                  />
                  <DetailItem
                    label={t("rashi")}
                    value={selectedProfile.rashi}
                  />
                  <DetailItem
                    label={t("ascendent")}
                    value={selectedProfile.ascendent}
                  />
                  <DetailItem
                    label={t("birthTime")}
                    value={selectedProfile.birth_time}
                  />
                  <DetailItem
                    label={t("birthPlace")}
                    value={selectedProfile.birth_place}
                  />
                </Section>
                <Section
                  title={t("preferences")}
                  isOpen={openSections.preferences}
                  toggle={() => toggleSection("preferences")}
                >
                  <DetailItem
                    label={t("preferredAgeRange")}
                    value={
                      selectedProfile.preferred_age_min &&
                      selectedProfile.preferred_age_max
                        ? `${selectedProfile.preferred_age_min} - ${selectedProfile.preferred_age_max}`
                        : t("notSpecified")
                    }
                  />
                  <DetailItem
                    label={t("preferredHeightRange")}
                    value={
                      selectedProfile.preferred_height_min &&
                      selectedProfile.preferred_height_max
                        ? `${selectedProfile.preferred_height_min} - ${selectedProfile.preferred_height_max}`
                        : t("notSpecified")
                    }
                  />
                  <DetailItem
                    label={t("preferredReligion")}
                    value={selectedProfile.preferred_religion}
                  />
                  <DetailItem
                    label={t("preferredCaste")}
                    value={selectedProfile.preferred_caste}
                  />
                  <DetailItem
                    label={t("preferredSubCaste")}
                    value={selectedProfile.preferred_sub_caste}
                  />
                  <DetailItem
                    label={t("preferredStatus")}
                    value={selectedProfile.preferred_work_status}
                  />
                  <DetailItem
                    label={t("preferredLocation")}
                    value={selectedProfile.preferred_location}
                  />
                </Section>
                <Section
                  title={t("documents")}
                  isOpen={openSections.documents}
                  toggle={() => toggleSection("documents")}
                >
                  {selectedProfile.horoscope_documents?.length > 0 ? (
                    selectedProfile.horoscope_documents.map(
                      (documentUrl, index) => (
                        <div key={`horoscope-${index}`} className="mt-4 mb-2">
                          <button
                            onClick={() => window.open(documentUrl, "_blank")}
                            className="flex items-center text-blue-500 hover:text-blue-700 transition-colors duration-200"
                          >
                            <DocumentIcon className="h-5 w-5 mr-2" />
                            <span>
                              {t("viewHoroscopeDocument", { index: index + 1 })}
                            </span>
                          </button>
                        </div>
                      ),
                    )
                  ) : (
                    <p>{t("noHoroscopeDocuments")}</p>
                  )}
                </Section>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showConfirmPopup && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[999]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl relative overflow-hidden"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <h3 className="text-lg font-semibold text-[#676666] mb-4">
                {t("confirmPointsSpend")}
              </h3>
              <p className="text-gray-600 mb-4">
                {t("spendPointsMessage", {
                  points: 100,
                  name: profileToView?.full_name,
                })}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmPopup(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  {t("noThanks")}
                </button>
                <button
                  onClick={confirmSpendPoints}
                  className="px-4 py-2 bg-[#fa6938] text-white rounded-md text-sm font-medium hover:bg-[#ff7142]"
                >
                  {t("confirm")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {showReportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[999]">
            <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">{t("reportProfile")}</h3>
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason("");
                  }}
                  className="p-1 rounded-lg hover:bg-gray-100"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("reportReason")}
                </label>
                <textarea
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FFA500]"
                  rows={4}
                  placeholder={t("reportReasonPlaceholder")}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason("");
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  {t("cancel")}
                </button>
                <button
                  onClick={handleReportProfile}
                  className="px-4 py-2 bg-[#fa6938] text-white rounded-md text-sm font-medium hover:bg-[#ff7142]"
                >
                  {t("submitReport")}
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {ShowPopupRechargePoints && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[999]"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={{ hidden: { opacity: 0 }, visible: { opacity: 1 } }}
          >
            <div
              className="relative bg-white rounded-xl max-w-2xl w-full p-4 overflow-y-auto max-h-[90vh] shadow-2xl"
              initial={{ scale: 0.9, opacity: 0, rotateX: -15, rotateY: -15 }}
              animate={{ scale: 1, opacity: 1, rotateX: 0, rotateY: 0 }}
              exit={{ scale: 0.9, opacity: 0, rotateX: -15, rotateY: -15 }}
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
            >
              <button
                onClick={() => setShowPopupRechargePoints(false)}
                className="p-1 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <XMarkIcon className="absolute right-2 top-2 h-8 w-8 font-semibold text-red-500 hover:bg-gray-100 p-1 rounded-lg" />
              </button>
              <p>{t("insufficientPoints")}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => navigate("/points")}
                  className="px-3 py-1.5 rounded-lg bg-[#fa6938] hover:bg-[#ff7142] active:bg-[#fa6938] mt-2 text-white font-medium"
                >
                  {t("goToRecharge")}
                </button>
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <Footer />
    </div>
  );
};

export default ViewAllProfiles;
